import ApiClient from '@/util/ApiClient'
import { claimNetworkError } from '@/util/api-error-snackbar'
import i18n from '@/util/config/i18n'
import { useSnackbarStore } from '@/modules/global/stores/SnackbarStore'
import { toSafeEmbedSrc } from '@/util/embed-click-to-load'

/**
 * Whether a page will show when framed as it is. Sites can refuse to be shown inside other sites (`X-Frame-Options`,
 * CSP `frame-ancestors`); the browser then shows its own "refused to connect" page, which the embedding page can
 * neither see nor read the headers behind. So the server checks before such a link is saved as an embed, and the user
 * is offered a bookmark instead.
 */

/** FRAME_BLOCKED and UNREACHABLE come from the server; INSECURE (not https, never framed by the app) is decided here */
export type EmbeddabilityReason = 'FRAME_BLOCKED' | 'UNREACHABLE' | 'INSECURE'

export type Embeddability = { embeddable: true; reason: null } | { embeddable: false; reason: EmbeddabilityReason }

/** What a link form's `submitUrl` resolves with when the link cannot be embedded but can be kept as a bookmark */
export type EmbedLinkRejection = { error: string; bookmarkUrl: string }

/** Resolution of a link form's `submitUrl`: nothing when saved, else an error message or a bookmark offer */
export type EmbedLinkSubmitResult = string | EmbedLinkRejection | void

// The server allows each fetch 5s and follows a few redirects; past this the link is let through rather than held up
const CHECK_TIMEOUT = 8000

const EMBEDDABLE: Embeddability = { embeddable: true, reason: null }
const SERVER_REASONS: EmbeddabilityReason[] = ['FRAME_BLOCKED', 'UNREACHABLE']

/**
 * Whether `url` can be framed. A link that is not https is refused at once: embeds only ever frame https pages (see
 * toSafeEmbedSrc), so saving one would leave a block that never loads. Otherwise the server decides; anything other
 * than a clear refusal from it (an older server without the check, a timeout, an error) lets the link through, as
 * before the check existed: the embed card explains a failure to load.
 */
export async function checkEmbeddable(url: string): Promise<Embeddability> {
  if (!toSafeEmbedSrc(url)) return { embeddable: false, reason: 'INSECURE' }
  try {
    const { data } = await ApiClient.post('/utilities/check-embeddable', { url }, { timeout: CHECK_TIMEOUT })
    if (data?.embeddable === false && SERVER_REASONS.includes(data.reason)) {
      return { embeddable: false, reason: data.reason }
    }
  } catch (error) {
    // Let the link through, with no generic network error: a timeout or an unreachable server is not the user's to fix
    claimNetworkError(error)
  }
  return EMBEDDABLE
}

/** The link's host as people write it, for the messages */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** Why the link form cannot embed `url` */
export function embedLinkCheckMessage(url: string, reason: EmbeddabilityReason): string {
  switch (reason) {
    case 'FRAME_BLOCKED':
      return i18n.t('embeds.linkCheck.frameBlocked', { host: hostOf(url) }) as string
    case 'INSECURE':
      return i18n.t('embeds.clickToLoad.blockedReason.insecure') as string
    default:
      return i18n.t('embeds.linkCheck.unreachable') as string
  }
}

/** A pasted link became a bookmark rather than the embed asked for: says why */
export function notifyEmbedAddedAsBookmark(url: string, reason: EmbeddabilityReason): void {
  let label: string
  switch (reason) {
    case 'FRAME_BLOCKED':
      label = i18n.t('embeds.linkCheck.bookmarkedFrameBlocked', { host: hostOf(url) }) as string
      break
    case 'INSECURE':
      label = i18n.t('embeds.linkCheck.bookmarkedInsecure') as string
      break
    default:
      label = i18n.t('embeds.linkCheck.bookmarkedUnreachable') as string
  }
  useSnackbarStore().enqueue({ label, variant: 'warning', dedupeKey: `embed-as-bookmark-${url}` })
}
